import _ from 'lodash';

import { toRecord } from 'shared/utils/to-record';

export const NEXT_DAY_SUFFIX = '(Next Day)';

export const timezoneOptions = [
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'US/Hawaii',
  'US/Alaska',
  'America/Anchorage',
  'America/Chicago',
  'America/Dawson',
  'America/Dawson_Creek',
  'America/Denver',
  'America/Detroit',
  'America/Edmonton',
  'America/Fort_Nelson',
  'America/Fort_Wayne',
  'America/Goose_Bay',
  'America/Halifax',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indianapolis',
  'America/Inuvik',
  'America/Iqaluit',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Knox_IN',
  'America/Los_Angeles',
  'America/Louisville',
  'America/Menominee',
  'America/New_York',
  'America/Nome',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Phoenix',
  'America/Puerto_Rico',
  'America/Regina',
  'America/Shiprock',
  'America/Sitka',
  'America/St_Barthelemy',
  'America/St_Johns',
  'America/Thunder_Bay',
  'America/Toronto',
  'America/Vancouver',
  'America/Virgin',
  'America/Whitehorse',
  'America/Winnipeg',
  'America/Yakutat',
  'America/Yellowknife',
  'Pacific/Honolulu',
] as const;

// Javascript day order.
export const dayNamesLong = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export type DayNameLong = (typeof dayNamesLong)[number];

export const daysLongToShortNames: Record<DayNameLong, string> = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tues',
  Wednesday: 'Weds',
  Thursday: 'Thurs',
  Friday: 'Fri',
  Saturday: 'Sat',
};

type DayOfWeek = { id: number; long: DayNameLong; short: string; value: DayNameLong };

// TODO: refactor dayNamesLong and daysLongToShortNames to use this
//  (current daysLongToShortNames contains some 4-letter names)
export const daysOfWeek: DayOfWeek[] = [
  { id: 0, long: 'Sunday', short: 'Sun', value: 'Sunday' },
  { id: 1, long: 'Monday', short: 'Mon', value: 'Monday' },
  { id: 2, long: 'Tuesday', short: 'Tue', value: 'Tuesday' },
  { id: 3, long: 'Wednesday', short: 'Wed', value: 'Wednesday' },
  { id: 4, long: 'Thursday', short: 'Thu', value: 'Thursday' },
  { id: 5, long: 'Friday', short: 'Fri', value: 'Friday' },
  { id: 6, long: 'Saturday', short: 'Sat', value: 'Saturday' },
];

export const dayIdToDayOfWeek: Record<number, DayNameLong> = toRecord(daysOfWeek, (day) => [day.id, day.value]);

export const timeBase = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'] as const;
export const dayTimeBreaks = [':00 AM', ':15 AM', ':30 AM', ':45 AM'] as const;
export const eveningTimeBreaks = [':00 PM', ':15 PM', ':30 PM', ':45 PM'] as const;
export const dayTimes: string[] = _.flatten(timeBase.map((tbase) => dayTimeBreaks.map((tbreak) => `${tbase}${tbreak}`)));
export const eveningTimes: string[] = _.flatten(
  timeBase.map((tbase) => eveningTimeBreaks.map((tbreak) => `${tbase}${tbreak}`))
);
export const times: string[] = _.concat(dayTimes, eveningTimes);

// Next day times for extended hours (up to 6 AM next day)
export const nextDayTimeBase = ['12', '1', '2', '3', '4', '5', '6'] as const;
export const nextDayTimeBreaks = [
  `:00 AM ${NEXT_DAY_SUFFIX}`,
  `:15 AM ${NEXT_DAY_SUFFIX}`,
  `:30 AM ${NEXT_DAY_SUFFIX}`,
  `:45 AM ${NEXT_DAY_SUFFIX}`,
];
export const nextDayTimes: string[] = _.flatten(
  nextDayTimeBase.map((tbase) => nextDayTimeBreaks.map((tbreak) => `${tbase}${tbreak}`))
);

// Combined times including next day options
export const timesWithNextDay: string[] = _.concat(times, nextDayTimes);

export const defaultDayStart = '8:00 AM';
export const defaultDayEnd = '8:00 PM';

type DayHoursRange = { start: string; end: string };

export const defaultHours: Record<DayNameLong, DayHoursRange> = toRecord(dayNamesLong, (day) => [
  day,
  { start: defaultDayStart, end: defaultDayEnd },
]);

/**
 * Reorders the keys of an object to match `dayNamesLong` order. Generic over
 * the input shape so callers get back exactly the type they passed in,
 * instead of a widened `Record<string, unknown>` that would need a cast at
 * every call site.
 */
export const orderDays = <T extends Record<string, unknown>>(unordered: T): T => {
  const ordered = {} as T;
  _.keys(unordered)
    .sort((a, b) => dayNamesLong.indexOf(a as DayNameLong) - dayNamesLong.indexOf(b as DayNameLong))
    .forEach((key) => {
      ordered[key as keyof T] = unordered[key as keyof T];
    });
  return ordered;
};

type TimeSlotIncrementOption = { key: string; label: string; value: number };

export const defaultScheduledOrderTimeSlotIncrement: TimeSlotIncrementOption = {
  key: '30 minutes',
  label: '30 minutes each',
  value: 30,
};
export const scheduledOrderTimeSlotIncrements: TimeSlotIncrementOption[] = [
  { key: '15 minutes', label: '15 minutes each', value: 15 },
  defaultScheduledOrderTimeSlotIncrement,
  { key: '1 hour', label: '1 hour each', value: 60 },
  { key: '1.5 hour', label: '1.5 hours each', value: 90 },
  { key: '2 hour', label: '2 hours each', value: 120 },
  { key: '2.5 hour', label: '2.5 hours each', value: 150 },
  { key: '3 hour', label: '3 hours each', value: 180 },
  { key: '3.5 hour', label: '3.5 hours each', value: 210 },
  { key: '4 hour', label: '4 hours each', value: 240 },
  { key: '4.5 hour', label: '4.5 hours each', value: 270 },
  { key: '5 hour', label: '5 hours each', value: 300 },
];
export const scheduledOrderNextAvailableOptions: { val: number; key: string }[] = _.map(
  _.range(5, 100, 5).concat(_.range(100, 301, 10)),
  (el) => ({
    val: el,
    key: `${el} minutes`,
  })
);

const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 1440;

type DaysHoursMinutes = { days: number; hours: number; minutes: number };

/** Converts a total minutes value into its days, hours, and minutes components. */
export function minutesToDaysHoursMinutes(totalMinutes: number | null | undefined): DaysHoursMinutes {
  const total = Math.max(0, Math.round(totalMinutes || 0));
  const days = Math.floor(total / MINUTES_PER_DAY);
  const hours = Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = total % MINUTES_PER_HOUR;
  return { days, hours, minutes };
}

/** Converts days, hours, and minutes components into a total minutes value. */
export function daysHoursMinutesToMinutes({ days = 0, hours = 0, minutes = 0 }: Partial<DaysHoursMinutes> = {}): number {
  return days * MINUTES_PER_DAY + hours * MINUTES_PER_HOUR + minutes;
}
